import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, resolveDynamicComponent as _resolveDynamicComponent, openBlock as _openBlock, createBlock as _createBlock, KeepAlive as _KeepAlive, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, normalizeClass as _normalizeClass, createElementBlock as _createElementBlock } from "vue"

import { computed, onBeforeMount, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'

import { emitter } from '@/services/mitt'
import { platform } from '@/services/platform'
import { isSafari as _isSafari } from '@/helpers/userAgent'
import { getProperLanguage, weglotInitialized } from '@/services/weglot'
import { backLink } from '@/models/backlink'


export default /*@__PURE__*/_defineComponent({
  __name: 'App',
  setup(__props) {

const route = useRoute()

const appRef = ref<HTMLElement | null>(null)

const isNativeApp = ref(false)
const isSafari = ref(false)
const currentLang = ref('en')

onBeforeMount(async () => {
  isNativeApp.value = await platform.isNativeApp()
  isSafari.value = (await platform.isIOS()) || _isSafari()

  if (weglotInitialized()) {
    listenForLanguageChanges()
  } else {
    window.addEventListener('weglot_initialized', () => {
      listenForLanguageChanges()
    })
  }
})

/**
 * A list of components from the navigation history that we
 * want to cache with `KeepAlive`.
 *
 * This list is dynamic instead of static because we can't manually
 * destroy cached components, but `KeepAlive` will destroy any caches
 * from components that are not in its `include` list, so by changing
 * the contents of this list, we can effectively control which caches
 * to destroy and which to keep.
 */
const keepAliveComponents = computed(() => {
  return backLink.value.keepAliveComponents
})

/**
 * Create a key for components inside `KeepAlive`.
 *
 * This is used to differentiate routes that use the
 * same component. Without it, they would share the
 * same cache.
 */
const componentKey = computed(() => {
  if (!route.meta?.keepAlive) {
    return
  }

  return route.path
})

/**
 * Change the value of `currentLang` whenever
 * the current language changes.
 */
function listenForLanguageChanges(): void {
  const weglot = (window as any).Weglot

  currentLang.value = getProperLanguage(weglot.getCurrentLang())

  weglot.on('languageChanged', (newLang: string) => {
    currentLang.value = newLang
  })
}

function resizeHeight(): void {
  // On mobile browsers, the address bar can reduce the viewport
  // height without reducing the vh unit.
  // So if we set the height to 100vh, the bottom may be obscured by
  // an address bar or navbar in the app.
  //
  // To work around this, we set the CSS variable --vh to 1/100
  // of the height of the actual viewport, and then set this
  // instead of 100vh:
  //   height: calc(var(--vh, 1vh) * 100);
  // More: https://chanind.github.io/javascript/2019/09/28/avoid-100vh-on-mobile-web.html
  const windowsVH = window.innerHeight / 100
  appRef.value?.style.setProperty('--vh', windowsVH + 'px')
}

onMounted(() => {
  resizeHeight()
  window.addEventListener('resize', () => {
    resizeHeight()
    emitResizeEvent()
  })

  window.addEventListener('orientationchange', () => {
    resizeHeight()
    emitResizeEvent()
  })
})

function emitResizeEvent(): void {
  emitter.emit('windowResize')
}

function onScroll(): void {
  emitter.emit('appScroll', appRef.value as HTMLElement)
}

return (_ctx: any,_cache: any) => {
  const _component_RouterView = _resolveComponent("RouterView")!

  return (_openBlock(), _createElementBlock("div", {
    id: "app",
    ref_key: "appRef",
    ref: appRef,
    class: _normalizeClass(["sf-app", {
      'native-app': isNativeApp.value,
      'sf-safari': isSafari.value,
      'sf-translated': currentLang.value !== 'en',
      [`sf-lang-${currentLang.value}`]: true,
    }]),
    onScroll: onScroll
  }, [
    _cache[0] || (_cache[0] = _createElementVNode("div", { id: "observer-screen-top-target" }, null, -1)),
    _cache[1] || (_cache[1] = _createElementVNode("div", { id: "iosFix" }, null, -1)),
    _createVNode(_component_RouterView, null, {
      default: _withCtx(({ Component }) => [
        (_openBlock(), _createBlock(_KeepAlive, {
          max: keepAliveComponents.value.length,
          include: keepAliveComponents.value
        }, [
          (_openBlock(), _createBlock(_resolveDynamicComponent(Component), { key: componentKey.value }))
        ], 1032, ["max", "include"]))
      ]),
      _: 1
    })
  ], 34))
}
}

})